import { useCallback, useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/shadcn/alert-dialog';
import { Button } from '@/components/shadcn/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shadcn/card';
import showToast from '@/components/ui/Toast';
import WhatsappSessionList from '@/components/community/whatsapp/WhatsappSessionList';
import whatsappCommunityService, {
  type WhatsappCommunitySession,
} from '@/services/whatsappCommunityService';

interface WhatsappSessionsTabProps {
  refreshKey?: number;
  onSessionsChange?: () => void;
}

const WhatsappSessionsTab = ({ refreshKey = 0, onSessionsChange }: WhatsappSessionsTabProps) => {
  const [sessions, setSessions] = useState<WhatsappCommunitySession[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [logoutTarget, setLogoutTarget] = useState<WhatsappCommunitySession | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const loadSessions = useCallback(async () => {
    setIsLoading(true);
    try {
      setSessions(await whatsappCommunityService.listSessions());
    } catch (error) {
      setSessions([]);
      showToast.error(
        error instanceof Error ? error.message : 'Gagal memuat sesi WhatsApp'
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSessions();
  }, [loadSessions, refreshKey]);

  const handleConfirmLogout = async () => {
    if (!logoutTarget) return;
    setIsLoggingOut(true);
    try {
      await whatsappCommunityService.deleteSession(logoutTarget.session_id);
      showToast.success('Perangkat WhatsApp berhasil diputus');
      setLogoutTarget(null);
      await loadSessions();
      onSessionsChange?.();
    } catch (error) {
      showToast.error(
        error instanceof Error ? error.message : 'Gagal memutus sesi WhatsApp'
      );
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="rounded-2xl border-[#E0E6ED] shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0 p-4 sm:p-5">
          <CardTitle className="text-base font-semibold text-[#0E1726]">
            Session terhubung
          </CardTitle>
          <Button
            type="button"
            variant="outline"
            className="h-10 shrink-0 rounded-xl"
            onClick={() => void loadSessions()}
          >
            <Icon icon="mdi:refresh" className="h-4 w-4" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        </CardHeader>

        <CardContent className="p-4 pt-0 sm:p-5 sm:pt-0">
          <WhatsappSessionList
            sessions={sessions}
            isLoading={isLoading}
            onLogout={setLogoutTarget}
          />
        </CardContent>
      </Card>

      <AlertDialog
        open={!!logoutTarget}
        onOpenChange={(open) => !open && !isLoggingOut && setLogoutTarget(null)}
      >
        <AlertDialogContent className="max-w-md rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Logout perangkat WhatsApp</AlertDialogTitle>
            <AlertDialogDescription>
              Putus koneksi WhatsApp untuk{' '}
              <span className="font-medium text-foreground">
                {logoutTarget?.device_name || logoutTarget?.phone_number || 'perangkat ini'}
              </span>
              ? Ini tidak mengeluarkan kamu dari aplikasi Sporto.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoggingOut} className="rounded-xl">
              Batal
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={isLoggingOut}
              className="rounded-xl bg-red-600 hover:bg-red-700"
              onClick={(e) => {
                e.preventDefault();
                void handleConfirmLogout();
              }}
            >
              {isLoggingOut ? 'Memutus...' : 'Logout'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default WhatsappSessionsTab;
